export interface BulkRunResult<T> {
  succeeded: T[];
  failed: { item: T; error: unknown }[];
}

export interface BulkRunOptions {
  concurrency?: number;
  signal?: AbortSignal;
}

const DEFAULT_CONCURRENCY = 4;

export class BulkRunAbortError extends Error {
  constructor() {
    super('Bulk run aborted');
    this.name = 'BulkRunAbortError';
  }
}

export async function bulkRun<T>(
  items: T[],
  worker: (item: T) => Promise<unknown>,
  options: BulkRunOptions = {},
): Promise<BulkRunResult<T>> {
  const { concurrency = DEFAULT_CONCURRENCY, signal } = options;
  const succeeded: T[] = [];
  const failed: { item: T; error: unknown }[] = [];

  let cursor = 0;

  const runOne = async (): Promise<void> => {
    while (cursor < items.length) {
      if (signal?.aborted) {
        throw new BulkRunAbortError();
      }
      const idx = cursor++;
      const item = items[idx];
      try {
        await worker(item);
        succeeded.push(item);
      } catch (error) {
        failed.push({ item, error });
      }
    }
  };

  const workers = Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, runOne);
  await Promise.all(workers);

  return { succeeded, failed };
}
